// Configuración → API para IAs (MCP + REST) — gestión de API keys y guía de conexión
// para Wingman de Emergent, ChatGPT, Claude, n8n y scripts externos.
import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  Bot, KeyRound, Plus, Copy, Ban, RotateCcw, Loader2, ShieldCheck,
  Plug, FileJson, TerminalSquare, CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';
import ConectorChatGPT from './oauth/ConectorChatGPT';

const SCOPE_LABELS = {
  '*': 'Acceso total',
  'ots:read': 'OTs/Proyectos · lectura (incl. costo/utilidad)',
  'ots:write': 'OTs/Proyectos · crear y corregir',
  'compras:read': 'Compras/OCs/Requisiciones · lectura',
  'compras:write': 'Requisiciones · crear, corregir, aprobar',
  'ventas:read': 'Ventas/Proformas/CxC · lectura',
  'ventas:write': 'Proformas · crear',
  'banco:read': 'Banco/Conciliación · lectura',
  'banco:write': 'Banco · conciliar movimientos',
  'cxp:read': 'Ctas. por Pagar · lectura',
  'stock:read': 'Stock · lectura',
  'dte:read': 'Facturas DTE · lectura',
  'proveedores:read': 'Proveedores · lectura',
  'rrhh:read': 'Partes diarios/Horas · lectura',
  'rrhh:write': 'Partes diarios · corregir/reabrir',
  'cotizaciones:read': 'Solic. cotización · lectura',
  'cotizaciones:write': 'Solic. cotización · corregir',
};

const copiar = (texto, label) => {
  navigator.clipboard.writeText(texto);
  toast.success(`${label} copiado`);
};

function UrlRow({ label, url, icon: Icon, testid }) {
  return (
    <div className="flex items-center gap-2 rounded-lg border bg-slate-50 px-3 py-2" data-testid={testid}>
      <Icon className="h-4 w-4 text-indigo-600 shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-medium text-muted-foreground">{label}</p>
        <p className="truncate font-mono text-xs">{url}</p>
      </div>
      <Button variant="ghost" size="sm" onClick={() => copiar(url, label)} data-testid={`${testid}-copy`}>
        <Copy className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

export default function ApiIAPage() {
  const [keys, setKeys] = useState([]);
  const [scopesDisponibles, setScopesDisponibles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [nombre, setNombre] = useState('');
  const [scopesSel, setScopesSel] = useState(['*']);
  const [creating, setCreating] = useState(false);
  const [nuevaKey, setNuevaKey] = useState(null);

  const base = window.location.origin;
  const mcpUrl = `${base}/api/mcp`;
  const restBase = `${base}/api/ia/v1`;
  const openapiUrl = `${base}/api/ia/v1/openapi.json`;

  const load = useCallback(async () => {
    try {
      const res = await api.get('/ia/keys');
      setKeys(res.data.keys || []);
      setScopesDisponibles(res.data.scopes_disponibles || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error cargando keys');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleScope = (s) => {
    setScopesSel((prev) => {
      if (s === '*') return prev.includes('*') ? [] : ['*'];
      const sin = prev.filter((x) => x !== '*' && x !== s);
      return prev.includes(s) ? sin : [...sin, s];
    });
  };

  const crearKey = async () => {
    if (!nombre.trim()) { toast.error('Ponle un nombre a la key'); return; }
    if (scopesSel.length === 0) { toast.error('Selecciona al menos un permiso'); return; }
    setCreating(true);
    try {
      const res = await api.post('/ia/keys', { nombre: nombre.trim(), scopes: scopesSel });
      setNuevaKey(res.data);
      setDialogOpen(false);
      setNombre('');
      setScopesSel(['*']);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error creando la key');
    } finally {
      setCreating(false);
    }
  };

  const revocar = async (k) => {
    try {
      await api.delete(`/ia/keys/${k.id}`);
      toast.success(`Key "${k.nombre}" revocada`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error revocando');
    }
  };

  const reactivar = async (k) => {
    try {
      await api.post(`/ia/keys/${k.id}/reactivar`);
      toast.success(`Key "${k.nombre}" reactivada`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error reactivando');
    }
  };

  return (
    <div className="space-y-6 p-6" data-testid="api-ia-page">
      <div className="flex items-center gap-3">
        <div className="rounded-xl bg-indigo-100 p-2"><Bot className="h-6 w-6 text-indigo-700" /></div>
        <div>
          <h1 className="text-2xl font-bold">API para IAs</h1>
          <p className="text-sm text-muted-foreground">
            Conecta Wingman, ChatGPT, Claude o tus propios agentes al ERP vía MCP o REST.
            Las escrituras son controladas: las requisiciones quedan pendientes de aprobación humana.
          </p>
        </div>
      </div>

      {nuevaKey && (
        <Card className="border-emerald-300 bg-emerald-50" data-testid="nueva-key-alert">
          <CardContent className="flex items-center gap-3 py-4">
            <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-emerald-900">
                Key &quot;{nuevaKey.nombre}&quot; creada — cópiala AHORA, no se volverá a mostrar:
              </p>
              <p className="mt-1 break-all rounded bg-white px-2 py-1 font-mono text-xs" data-testid="nueva-key-valor">
                {nuevaKey.key}
              </p>
            </div>
            <Button size="sm" onClick={() => copiar(nuevaKey.key, 'API key')} data-testid="nueva-key-copy">
              <Copy className="mr-1 h-3.5 w-3.5" /> Copiar
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setNuevaKey(null)} data-testid="nueva-key-cerrar">
              Cerrar
            </Button>
          </CardContent>
        </Card>
      )}

      <ConectorChatGPT />

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <KeyRound className="h-4 w-4" /> API Keys <span className="text-xs font-normal text-muted-foreground">(Wingman, Claude, n8n, scripts)</span>
          </CardTitle>
          <Button size="sm" onClick={() => setDialogOpen(true)} data-testid="btn-nueva-key">
            <Plus className="mr-1 h-4 w-4" /> Nueva API Key
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <Skeleton className="h-24 w-full" />
          ) : keys.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground" data-testid="keys-empty">
              Aún no hay keys. Crea la primera para conectar una IA.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="py-2 pr-3">Nombre</th>
                    <th className="py-2 pr-3">Key</th>
                    <th className="py-2 pr-3">Permisos</th>
                    <th className="py-2 pr-3">Estado</th>
                    <th className="py-2 pr-3">Último uso</th>
                    <th className="py-2 pr-3">Requests</th>
                    <th className="py-2" />
                  </tr>
                </thead>
                <tbody>
                  {keys.map((k) => (
                    <tr key={k.id} className="border-b last:border-0" data-testid={`key-row-${k.nombre}`}>
                      <td className="py-2 pr-3 font-medium">{k.nombre}</td>
                      <td className="py-2 pr-3 font-mono text-xs">{k.prefijo}</td>
                      <td className="py-2 pr-3">
                        <div className="flex max-w-[280px] flex-wrap gap-1">
                          {(k.scopes || []).map((s) => (
                            <Badge key={s} variant="outline" className="text-[10px]">
                              {SCOPE_LABELS[s] || s}
                            </Badge>
                          ))}
                        </div>
                      </td>
                      <td className="py-2 pr-3">
                        {k.activa
                          ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Activa</Badge>
                          : <Badge variant="secondary" className="text-muted-foreground">Revocada</Badge>}
                      </td>
                      <td className="py-2 pr-3 text-xs text-muted-foreground">
                        {k.last_used_at ? new Date(k.last_used_at).toLocaleString('es-CL') : '—'}
                      </td>
                      <td className="py-2 pr-3 text-xs">{k.total_requests || 0}</td>
                      <td className="py-2 text-right">
                        {k.activa ? (
                          <Button variant="ghost" size="sm" className="text-red-600" onClick={() => revocar(k)}
                            data-testid={`btn-revocar-${k.nombre}`}>
                            <Ban className="mr-1 h-3.5 w-3.5" /> Revocar
                          </Button>
                        ) : (
                          <Button variant="ghost" size="sm" onClick={() => reactivar(k)}
                            data-testid={`btn-reactivar-${k.nombre}`}>
                            <RotateCcw className="mr-1 h-3.5 w-3.5" /> Reactivar
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Plug className="h-4 w-4" /> URLs de conexión
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <UrlRow label="Servidor MCP (Wingman / ChatGPT / Claude)" url={mcpUrl} icon={Bot} testid="url-mcp" />
            <UrlRow label="REST base" url={restBase} icon={TerminalSquare} testid="url-rest" />
            <UrlRow label="OpenAPI (ChatGPT Actions / n8n)" url={openapiUrl} icon={FileJson} testid="url-openapi" />
            <p className="pt-1 text-xs text-muted-foreground">
              Autenticación en todas las capas: header <span className="font-mono">X-API-Key: &lt;tu key&gt;</span>
              {' '}(también acepta <span className="font-mono">Authorization: Bearer</span>).
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <ShieldCheck className="h-4 w-4" /> Cómo conectar
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div data-testid="guia-wingman">
              <p className="font-semibold">Wingman (Emergent)</p>
              <p className="text-muted-foreground">
                Dile: <em>&quot;Conéctate a mi servidor MCP: {mcpUrl} usando el header X-API-Key&quot;</em> y pega tu key
                cuando te la pida. Wingman descubre las herramientas automáticamente.
              </p>
            </div>
            <div data-testid="guia-chatgpt">
              <p className="font-semibold">ChatGPT</p>
              <p className="text-muted-foreground">
                Conector MCP personalizado con la URL MCP y autenticación <strong>OAuth</strong> (ver &quot;Conectar ChatGPT&quot; arriba).
                Las API keys siguen sirviendo para Actions de GPTs (OpenAPI, header X-API-Key).
              </p>
            </div>
            <div data-testid="guia-curl">
              <p className="font-semibold">Prueba rápida (curl)</p>
              <pre className="overflow-x-auto rounded bg-slate-900 p-2 font-mono text-[11px] text-slate-100">
{`curl -H "X-API-Key: TU_KEY" \\
  "${restBase}/info"`}
              </pre>
            </div>
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
              Seguridad: las IAs no pueden generar OCs ni pagos. Crear requisiciones deja el registro
              PENDIENTE para que un humano lo apruebe en el ERP. Todo queda auditado.
            </p>
          </CardContent>
        </Card>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="dialog-nueva-key">
          <DialogHeader>
            <DialogTitle>Nueva API Key</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium">Nombre (quién la usa)</label>
              <Input value={nombre} onChange={(e) => setNombre(e.target.value)}
                placeholder="Ej: Wingman Renzo, ChatGPT Compras" data-testid="input-key-nombre" />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium">Permisos</label>
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-medium">
                  <Checkbox checked={scopesSel.includes('*')} onCheckedChange={() => toggleScope('*')}
                    data-testid="scope-todos" />
                  Acceso total (todos los permisos)
                </label>
                <div className="grid grid-cols-2 gap-2 pl-1">
                  {scopesDisponibles.map((s) => (
                    <label key={s} className="flex items-center gap-2 text-xs">
                      <Checkbox checked={scopesSel.includes('*') || scopesSel.includes(s)}
                        disabled={scopesSel.includes('*')}
                        onCheckedChange={() => toggleScope(s)}
                        data-testid={`scope-${s.replace(':', '-')}`} />
                      {SCOPE_LABELS[s] || s}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={crearKey} disabled={creating} data-testid="btn-crear-key">
              {creating ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Plus className="mr-1 h-4 w-4" />}
              Crear key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
